import Button from '@jetbrains/ring-ui/components/button/button'
import classNames from 'classnames'
import plur from 'plur'
import {useContext, useMemo, useReducer} from 'react'

import {restApi} from '../../../../services/rest'
import {ROOT_PROJECT_ID, stringifyId} from '../../../../types'
import {emptyArray} from '../../../../utils/empty'
import {GetRulesArgContext} from '../CleanupProjectPage.contexts'
import policiesStyles from '../CleanupProjectPage.css'
import {useIsReadOnly} from '../CleanupProjectPage.hooks'
import {getHolderNode} from '../CleanupProjectPage.utils'
import HolderItem from '../HolderItem/HolderItem.container'

import ListingPolicies from './Listing.policies'
import RulesListing from './Listing.rules'
import type {Props} from './Listing.types'
import {isPolicyCustom} from './Listing.utils'
import {getKey, getRulesByOwnership} from './Rules/Rules.utils'

function Listing({
  policy,
  type,
  owner,
  onPolicyReset,
  onPolicyClick,
  onPolicyDisable,
  readOnlyProjectsKey,
}: Props) {
  const readOnly = useIsReadOnly(owner.externalId, readOnlyProjectsKey)
  const [inheritedVisible, onToggleInherited] = useReducer(state => !state, false)
  const customPolicy = policy.externalId === ROOT_PROJECT_ID || isPolicyCustom(policy)
  const arg = useContext(GetRulesArgContext)
  const {rules} = restApi.endpoints.getCleanupRules.useQuery(arg, {
    selectFromResult: ({data}) => ({
      rules: data?.[getKey(getHolderNode(policy, type))]?.rules ?? emptyArray,
    }),
  })
  const rulesByOwnership = getRulesByOwnership(rules)
  const inheritedRulesCount =
    rulesByOwnership.inherited.length + rulesByOwnership.enforced.length + (customPolicy ? 0 : 1)
  const disabledInherited = rulesByOwnership.inherited.filter(item => item.ruleDisabled).length
  const disabledEnforced = rulesByOwnership.enforced.filter(item => item.ruleDisabled).length
  const policyDisabled = policy.options.cleanupPoliciesDisabled
  const inheritedDisabledRules = disabledInherited + disabledEnforced + (policyDisabled ? 1 : 0)
  const policyItem = useMemo(
    () => (
      <ListingPolicies
        inherited={!customPolicy}
        onPolicyClick={onPolicyClick}
        onPolicyReset={onPolicyReset}
        onPolicyDisable={onPolicyDisable}
        policy={policy}
        owner={owner}
        type={type}
        readOnly={readOnly}
      />
    ),
    [customPolicy, onPolicyClick, onPolicyDisable, onPolicyReset, owner, policy, readOnly, type],
  )
  const nameChunks = policy.name.split(/ \/ /g)
  const name = nameChunks[nameChunks.length - 1]
  return type !== 'template' || rulesByOwnership.own.length > 0 ? (
    <div
      key={stringifyId(policy.internalId)}
      id={policy.externalId}
      className={policiesStyles.policy}
    >
      <HolderItem
        {...policy}
        name={name}
        className={policiesStyles.holder}
        type={type}
        owner={owner}
      />
      <div className={policiesStyles.listings}>
        <RulesListing
          policy={policy}
          owner={owner}
          type={type}
          showInherited={false}
          hideInherited
          hideEnforced
          readOnlyProjectsKey={readOnlyProjectsKey}
        />
        {customPolicy && policyItem}
        {inheritedRulesCount > 0 && (
          <div className={policiesStyles.inheritedContainer}>
            <Button
              className={classNames(policiesStyles.toggleInherited)}
              onClick={onToggleInherited}
              text
            >{`${inheritedVisible ? 'Hide' : 'Show'} ${inheritedRulesCount} inherited ${
              inheritedDisabledRules ? `(${inheritedDisabledRules} disabled) ` : ''
            }${plur('rule', inheritedRulesCount)}`}</Button>
            {inheritedVisible && (
              <div className={classNames(policiesStyles.inheritedVisible)}>
                <RulesListing
                  policy={policy}
                  owner={owner}
                  type={type}
                  showInherited
                  hideOwn
                  readOnlyProjectsKey={readOnlyProjectsKey}
                />
                {!customPolicy && policyItem}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  ) : null
}
export default Listing
